if (!customElements.get('vee-instafeed')) {
  customElements.define(
    'vee-instafeed',
    class VeeInstafeed extends HTMLElement {
      connectedCallback() {
        this.swiperContainer = this.querySelector('[data-feed-carousel]');
        this.slides = this.querySelectorAll('[data-feed-slide]');
        this.initSwiper();
        this.initVideos();
        this.initTaggedProducts();
      }

      disconnectedCallback() {
        if (this.videoObserver) this.videoObserver.disconnect();
        if (this.swiper) this.swiper.destroy(true, true);
        if (this.outsideClickHandler) document.removeEventListener('click', this.outsideClickHandler);
      }

      get slidesPerViewDesktop() {
        return Number(this.dataset.slidesDesktop) || 1;
      }

      get slidesPerViewMobile() {
        return Number(this.dataset.slidesMobile) || 1;
      }

      get autoplayEnabled() {
        return this.dataset.autoplay === 'true';
      }

      get feedSize() {
        return Number(this.dataset.feedSize) || this.slides.length;
      }

      get desktopStatic() {
        return this.hasAttribute('data-desktop-static');
      }

      initSwiper() {
        if (!this.swiperContainer || typeof Swiper === 'undefined') return;
        if (this.feedSize === 1) return;

        const desktopDisabled = this.feedSize < 3 || this.desktopStatic;
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const swiperOptions = {
          slidesPerView: this.slidesPerViewMobile,
          spaceBetween: 0,
          observer: true,
          observeParents: true,
          a11y: true,
          keyboard: { enabled: true, onlyInViewport: true },
          mousewheel: { forceToAxis: true },
          pagination: {
            el: this.querySelector('[data-progress-bar]'),
            type: 'progressbar',
          },
          navigation: {
            prevEl: this.querySelector('[data-feed-prev]'),
            nextEl: this.querySelector('[data-feed-next]'),
          },
          breakpoints: {
            990: {
              slidesPerView: this.slidesPerViewDesktop,
              enabled: !desktopDisabled,
            },
          },
        };
        if (prefersReducedMotion) swiperOptions.speed = 0;

        this.swiper = new Swiper(this.swiperContainer, swiperOptions);
      }

      initVideos() {
        this.videoWrappers = this.querySelectorAll('[data-feed-video]');
        if (this.videoWrappers.length === 0) return;

        this.videoWrappers.forEach((wrapper) => {
          const video = wrapper.querySelector('video');
          if (video) {
            video.addEventListener('error', () => this.handleVideoError(wrapper, video), { once: true });
          }
        });

        if (this.autoplayEnabled) {
          // A ratio of exactly 1 is rarely reached on mobile (fractional slide widths, URL bar resizing),
          // so treat a mostly visible video as in view
          this.videoObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                const video = entry.target;
                if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                  this.playVideo(video);
                } else {
                  video.pause();
                }
              });
            },
            { threshold: [0, 0.6] }
          );

          this.videoWrappers.forEach((wrapper) => {
            const video = wrapper.querySelector('video');
            if (!video) return;
            // Mobile autoplay requires a muted inline video; set the properties too in case markup came via innerHTML (product-rerender)
            video.muted = true;
            video.defaultMuted = true;
            video.playsInline = true;
            this.videoObserver.observe(video);
          });
        } else {
          this.videoWrappers.forEach((wrapper) => this.initVideoToggle(wrapper));
        }
      }

      // Autoplay can still be blocked (e.g. iOS Low Power Mode), so fall back to tap-to-play
      playVideo(video) {
        const promise = video.play();
        if (!promise) return;
        promise.catch(() => {
          if (video.dataset.tapToPlay) return;
          video.dataset.tapToPlay = 'true';
          video.addEventListener('click', () => (video.paused ? video.play().catch(() => {}) : video.pause()));
        });
      }

      handleVideoError(wrapper, video) {
        if (this.videoObserver) this.videoObserver.unobserve(video);

        const fallbackImage = wrapper.querySelector('[data-video-fallback]');
        if (fallbackImage) {
          video.hidden = true;
          fallbackImage.hidden = false;
          const toggle = wrapper.querySelector('[data-video-toggle]');
          if (toggle) toggle.hidden = true;
        } else {
          const slide = wrapper.closest('[data-feed-slide]');
          if (slide) slide.hidden = true;
        }

        if (this.swiper) this.swiper.update();
      }

      initVideoToggle(wrapper) {
        const video = wrapper.querySelector('video');
        const button = wrapper.querySelector('[data-video-toggle]');
        const playIcon = wrapper.querySelector('[data-icon="play"]');
        const pauseIcon = wrapper.querySelector('[data-icon="pause"]');
        if (!video || !button) return;

        button.addEventListener('click', () => {
          if (video.paused) {
            video.muted = false;
            video.play();
          } else {
            video.pause();
          }
        });

        video.addEventListener('play', () => {
          wrapper.classList.add('is-playing');
          wrapper.classList.remove('is-paused');
          if (playIcon) playIcon.hidden = true;
          if (pauseIcon) pauseIcon.hidden = false;
          button.setAttribute('aria-label', 'Pause video');
        });

        video.addEventListener('pause', () => {
          wrapper.classList.add('is-paused');
          wrapper.classList.remove('is-playing');
          if (playIcon) playIcon.hidden = false;
          if (pauseIcon) pauseIcon.hidden = true;
          button.setAttribute('aria-label', 'Play video');
        });
      }

      initTaggedProducts() {
        this.popups = this.querySelectorAll('.tagged-products-popup');
        this.tagButtons = this.querySelectorAll('[data-tag-button]');

        this.tagButtons.forEach((button) => {
          button.addEventListener('click', (event) => {
            event.stopPropagation();
            const popup = button.closest('.post-controlls')?.querySelector('.tagged-products-popup');
            this.openPopup(popup);
          });
        });

        this.popups.forEach((popup) => {
          const closeButton = popup.querySelector('[data-popup-close]');
          if (closeButton) closeButton.addEventListener('click', () => this.closePopup(popup));

          popup.querySelectorAll('[variant-id]').forEach((button) => {
            button.addEventListener('click', () => this.addToCart(button));
          });
        });

        this.outsideClickHandler = (event) => {
          this.popups.forEach((popup) => {
            if (popup.classList.contains('active') && !popup.contains(event.target) && !event.target.closest('[data-tag-button]')) {
              this.closePopup(popup);
            }
          });
        };
        document.addEventListener('click', this.outsideClickHandler);
      }

      openPopup(popup) {
        if (!popup) return;
        this.popups.forEach((item) => item.classList.remove('active'));
        popup.classList.add('active');
      }

      closePopup(popup) {
        if (popup) popup.classList.remove('active');
      }

      // Tagged products come from the Instafeed app, so the page doesn't know their stock for the shopper's region.
      // Asked right before adding, from the product's ?view=stock template (same rule as the product page).
      // Returns true when the check can't be made, so a failed lookup never blocks a purchase.
      async isInStock(button, variantId) {
        const handle = button.dataset.handle;
        if (!handle) return true;
        try {
          const response = await fetch(`${window.Shopify.routes.root}products/${encodeURIComponent(handle)}?view=stock`, {
            credentials: 'same-origin',
          });
          if (!response.ok) return true;
          const stock = await response.json();
          return stock[variantId] !== false;
        } catch (error) {
          return true;
        }
      }

      markSoldOut(button) {
        button.textContent = button.dataset.soldOutLabel || button.textContent;
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('is-sold-out');
      }

      async addToCart(button) {
        const variantId = button.getAttribute('variant-id');
        if (!variantId || button.getAttribute('aria-disabled') === 'true') return;
        button.disabled = true;

        try {
          if (!(await this.isInStock(button, variantId))) {
            this.markSoldOut(button);
            return;
          }

          const response = await fetch(theme.routes.cart_add_url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ id: variantId, quantity: 1, sections: 'cart-icon-bubble,cart-drawer' }),
          });

          const result = await response.json();

          if (!response.ok) {
            throw new Error(result.description || result.message || 'Unable to add product to cart');
          }

          document.dispatchEvent(
            new CustomEvent('switch:cart:afteradditem', {
              bubbles: true,
              detail: { response: result },
            })
          );
        } catch (error) {
          console.error(error);
        } finally {
          button.disabled = false;
        }
      }
    }
  );
}
